import { Fragment, useEffect, useMemo, useState, type ReactElement } from "react";
import {
  ArrowLeftRight,
  Bell,
  CalendarClock,
  Check,
  CircleCheck,
  ClipboardCheck,
  ClipboardList,
  Coins,
  CreditCard,
  Factory,
  FileSpreadsheet,
  FileText,
  Headphones,
  ListChecks,
  Mail,
  MessageCircle,
  MessagesSquare,
  Package,
  PackageCheck,
  PackageSearch,
  Plus,
  Printer,
  Receipt,
  Shirt,
  ShoppingCart,
  SlidersHorizontal,
  Smartphone,
  Star,
  Ticket,
  Truck,
  TriangleAlert,
  Volume2,
  Wallet,
  Workflow,
} from "lucide-react";
import {
  obterPreferenciasNotificacoesUsuario,
  salvarPreferenciasNotificacoesUsuario,
  type CanaisNotificacao,
  type CanalNotificacao,
  type ChaveNotificacaoDisponivel,
  type PreferenciasPorCanal,
} from "../api/usuario";
import { useIdioma } from "../i18n/IdiomaProvider";
import { ModalCadastro } from "../components/ui/ModalCadastro";
import { BotaoContorno } from "../components/ui/BotaoAcao";
import { BotaoTopoPrimario } from "../components/ui/BotoesTopo";
import { TogglePadrao } from "../components/ui/TogglePadrao";
import { useRecursosApi } from "../utils/recursosApi";
import { definirSomNotificacoesAtivo } from "../utils/somNovaMensagem";
import { definirPushNotificacoesAtivo } from "../utils/avisoNotificacao";
import styles from "./PreferenciasNotificacoesModal.module.css";

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

type IconeSimples = (props: { size?: number }) => ReactElement;

const CANAIS: Array<{ chave: CanalNotificacao; rotulo: string; Icone: IconeSimples }> = [
  { chave: "email", rotulo: "E-mail", Icone: ({ size = 18 }) => <Mail size={size} strokeWidth={1.8} /> },
  { chave: "push", rotulo: "Push", Icone: ({ size = 18 }) => <Smartphone size={size} strokeWidth={1.8} /> },
  { chave: "whatsapp", rotulo: "WhatsApp", Icone: ({ size = 18 }) => <MessageCircle size={size} strokeWidth={1.8} /> },
];

const OPCOES: Array<{
  chave: ChaveNotificacaoDisponivel;
  titulo: string;
  descricao: string;
  Icone: IconeSimples;
}> = [
  { chave: "chamadosNovos", titulo: "Novos chamados", descricao: "Receber aviso quando um novo chamado chegar.", Icone: ({ size = 16 }) => <Plus size={size} strokeWidth={1.9} /> },
  { chave: "chamadosEncerrados", titulo: "Chamados encerrados", descricao: "Receber aviso quando um chamado for concluído.", Icone: ({ size = 16 }) => <CircleCheck size={size} strokeWidth={1.9} /> },
  { chave: "estoqueBaixo", titulo: "Estoque baixo", descricao: "Avisar quando um produto atingir o estoque mínimo.", Icone: ({ size = 16 }) => <Package size={size} strokeWidth={1.9} /> },
  { chave: "novaVenda", titulo: "Novas vendas", descricao: "Avisar quando uma nova venda for concluída.", Icone: ({ size = 16 }) => <Wallet size={size} strokeWidth={1.9} /> },
  { chave: "comprasNovas", titulo: "Novas compras", descricao: "Avisar quando uma nova compra for registrada.", Icone: ({ size = 16 }) => <ShoppingCart size={size} strokeWidth={1.9} /> },
  { chave: "parcelasAtrasadas", titulo: "Parcelas em atraso", descricao: "Avisar sobre parcelas vencidas que continuam pendentes.", Icone: ({ size = 16 }) => <Receipt size={size} strokeWidth={1.9} /> },
  { chave: "confirmacaoPagamento", titulo: "Confirmações de pagamento", descricao: "Avisar quando o pagamento de uma parcela for confirmado.", Icone: ({ size = 16 }) => <CreditCard size={size} strokeWidth={1.9} /> },
  { chave: "pedidosInsumosClientes", titulo: "Pedidos de insumos dos clientes", descricao: "Avisar quando um cliente enviar um novo pedido de insumos.", Icone: ({ size = 16 }) => <PackageSearch size={size} strokeWidth={1.9} /> },
  { chave: "pedidosSuporteClientes", titulo: "Pedidos de suporte dos clientes", descricao: "Avisar quando um cliente enviar um novo pedido de suporte.", Icone: ({ size = 16 }) => <Headphones size={size} strokeWidth={1.9} /> },
  { chave: "vencimentoInsumos", titulo: "Vencimento de insumos", descricao: "Avisar quando um insumo estiver próximo do vencimento.", Icone: ({ size = 16 }) => <CalendarClock size={size} strokeWidth={1.9} /> },
  /* Tickets, Produção e Protocolo. Cada item só aparece para quem tem a tela do
     setor liberada no Perfil de Acesso — o servidor devolve as opções em
     `opcoesDisponiveis`. */
  { chave: "tarefasSuporte", titulo: "Agendamentos de tickets", descricao: "Avisar quando um agendamento do quadro de tickets for criado ou movido.", Icone: ({ size = 16 }) => <Ticket size={size} strokeWidth={1.9} /> },
  { chave: "checklistsSuporte", titulo: "Checklists de tickets", descricao: "Avisar quando um checklist for atribuído ou concluído.", Icone: ({ size = 16 }) => <ListChecks size={size} strokeWidth={1.9} /> },
  { chave: "formatacaoNova", titulo: "Novas formatações", descricao: "Avisar quando uma formatação for criada na produção.", Icone: ({ size = 16 }) => <SlidersHorizontal size={size} strokeWidth={1.9} /> },
  { chave: "producaoMovimentacao", titulo: "Movimentações da produção", descricao: "Avisar quando um cartão mudar de etapa no controle de produção.", Icone: ({ size = 16 }) => <Workflow size={size} strokeWidth={1.9} /> },
  { chave: "producaoEtapaConcluida", titulo: "Etapas concluídas", descricao: "Avisar quando uma etapa da produção for concluída.", Icone: ({ size = 16 }) => <CircleCheck size={size} strokeWidth={1.9} /> },
  { chave: "producaoPrazoProximo", titulo: "Prazo próximo da produção", descricao: "Avisar nos três dias finais quando ainda houver etapas pendentes.", Icone: ({ size = 16 }) => <CalendarClock size={size} strokeWidth={1.9} /> },
  { chave: "producaoEmAtraso", titulo: "Produção em atraso", descricao: "Avisar quantos dias uma formatação incompleta está atrasada.", Icone: ({ size = 16 }) => <TriangleAlert size={size} strokeWidth={1.9} /> },
  { chave: "protocoloRegistrado", titulo: "Protocolos registrados", descricao: "Avisar quando um protocolo de retirada ou recebimento for registrado.", Icone: ({ size = 16 }) => <ClipboardCheck size={size} strokeWidth={1.9} /> },
  /* Telas criadas depois do catálogo inicial — atendimento, vendas, produção,
     estoque, compras e financeiro. */
  { chave: "mensagensAtendimento", titulo: "Mensagens do atendimento", descricao: "Avisar quando chegar uma nova mensagem nos canais de atendimento.", Icone: ({ size = 16 }) => <MessagesSquare size={size} strokeWidth={1.9} /> },
  { chave: "chatInterno", titulo: "Chat interno", descricao: "Avisar quando um colega enviar mensagem no chat da equipe.", Icone: ({ size = 16 }) => <MessageCircle size={size} strokeWidth={1.9} /> },
  { chave: "avaliacoesAtendimento", titulo: "Avaliações de atendimento", descricao: "Avisar quando um cliente avaliar o atendimento recebido.", Icone: ({ size = 16 }) => <Star size={size} strokeWidth={1.9} /> },
  { chave: "orcamentosNovos", titulo: "Orçamentos", descricao: "Avisar quando um orçamento for criado, aprovado ou vencer.", Icone: ({ size = 16 }) => <FileText size={size} strokeWidth={1.9} /> },
  { chave: "pedidosCliente", titulo: "Pedidos de clientes", descricao: "Avisar quando um cliente enviar um pedido pela Área do Cliente.", Icone: ({ size = 16 }) => <ClipboardList size={size} strokeWidth={1.9} /> },
  { chave: "entregasVenda", titulo: "Entregas", descricao: "Avisar sobre entregas planejadas, enviadas e concluídas.", Icone: ({ size = 16 }) => <Truck size={size} strokeWidth={1.9} /> },
  { chave: "cobrancasVenda", titulo: "Cobranças", descricao: "Avisar quando uma cobrança for gerada, paga ou vencer.", Icone: ({ size = 16 }) => <Receipt size={size} strokeWidth={1.9} /> },
  { chave: "fichaTecnica", titulo: "Fichas técnicas", descricao: "Avisar quando uma ficha técnica de fardamento for salva ou exportada.", Icone: ({ size = 16 }) => <Shirt size={size} strokeWidth={1.9} /> },
  { chave: "documentosFiscais", titulo: "Documentos fiscais", descricao: "Avisar sobre emissão, retorno e recebimento de notas fiscais.", Icone: ({ size = 16 }) => <FileSpreadsheet size={size} strokeWidth={1.9} /> },
  { chave: "impressoesDtf", titulo: "Impressões DTF", descricao: "Avisar quando o fechamento diário de impressões for registrado.", Icone: ({ size = 16 }) => <Printer size={size} strokeWidth={1.9} /> },
  { chave: "avariasProducao", titulo: "Avarias da produção", descricao: "Avisar quando uma avaria, defeito ou erro for registrado.", Icone: ({ size = 16 }) => <TriangleAlert size={size} strokeWidth={1.9} /> },
  { chave: "comissoesProducao", titulo: "Comissões", descricao: "Avisar quando as comissões de produção forem apuradas ou pagas.", Icone: ({ size = 16 }) => <Coins size={size} strokeWidth={1.9} /> },
  { chave: "expedicaoProducao", titulo: "Expedição", descricao: "Avisar quando um pedido for separado ou liberado na expedição.", Icone: ({ size = 16 }) => <PackageCheck size={size} strokeWidth={1.9} /> },
  { chave: "checklistsProducao", titulo: "Checklists da produção", descricao: "Avisar quando um checklist da produção for atribuído ou concluído.", Icone: ({ size = 16 }) => <ListChecks size={size} strokeWidth={1.9} /> },
  { chave: "oficinasServicos", titulo: "Serviços em oficinas", descricao: "Avisar quando um serviço for enviado ou retornar de uma oficina.", Icone: ({ size = 16 }) => <Factory size={size} strokeWidth={1.9} /> },
  { chave: "inventarioEstoque", titulo: "Inventário e ajustes", descricao: "Avisar quando um inventário ou ajuste de estoque for finalizado.", Icone: ({ size = 16 }) => <ClipboardCheck size={size} strokeWidth={1.9} /> },
  { chave: "transferenciasEstoque", titulo: "Transferências de estoque", descricao: "Avisar quando uma transferência entre locais for registrada.", Icone: ({ size = 16 }) => <ArrowLeftRight size={size} strokeWidth={1.9} /> },
  { chave: "cotacoesCompras", titulo: "Cotações de compras", descricao: "Avisar quando uma cotação receber propostas ou vencer.", Icone: ({ size = 16 }) => <PackageSearch size={size} strokeWidth={1.9} /> },
  { chave: "despesasVencendo", titulo: "Despesas a vencer", descricao: "Avisar sobre despesas próximas do vencimento ou em atraso.", Icone: ({ size = 16 }) => <Wallet size={size} strokeWidth={1.9} /> },
  { chave: "movimentoCaixa", titulo: "Caixa", descricao: "Avisar sobre abertura, sangria e conferência do caixa.", Icone: ({ size = 16 }) => <CreditCard size={size} strokeWidth={1.9} /> },
];

/* Notificação é opt-in por canal: enquanto as preferências do usuário não chegam do
   servidor, mostra o mesmo padrão que todo login novo recebe — push já ativo, os
   demais canais desligados até o usuário optar por eles. */
const CANAIS_PADRAO: CanaisNotificacao = { email: false, push: true, whatsapp: false };

export function PreferenciasNotificacoesModal({ aberto, onFechar }: Props) {
  return (
    <ModalCadastro
      aberto={aberto}
      onFechar={onFechar}
      titulo="Preferências de notificações"
      subtitulo="Escolha quais notificações deseja receber por canal."
      icone={<Bell size={22} strokeWidth={1.9} />}
      largura={1080}
    >
      <CorpoPreferenciasNotificacoes ativo={aberto} onFechar={onFechar} />
    </ModalCadastro>
  );
}

/**
 * Corpo das preferências: os canais no topo (com o resumo ao lado) e, abaixo, a
 * grade de cada notificação por canal. Salvar é explícito — nada é gravado a
 * cada clique.
 */
export function CorpoPreferenciasNotificacoes({
  ativo = true,
  onFechar,
}: {
  ativo?: boolean;
  onFechar?: () => void;
}) {
  const { traduzir } = useIdioma();
  const recursosApi = useRecursosApi();
  const [canais, setCanais] = useState<CanaisNotificacao>(CANAIS_PADRAO);
  const [somAtivo, setSomAtivo] = useState(true);
  const [porCanal, setPorCanal] = useState<PreferenciasPorCanal | null>(null);
  const [opcoesDisponiveis, setOpcoesDisponiveis] = useState<ChaveNotificacaoDisponivel[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!ativo) return;
    let vivo = true;
    setCarregando(true);
    setErro("");
    void obterPreferenciasNotificacoesUsuario()
      .then((dados) => {
        if (!vivo) return;
        setCanais(dados.canais);
        setSomAtivo(dados.somAtivo);
        definirSomNotificacoesAtivo(dados.somAtivo);
        definirPushNotificacoesAtivo(dados.canais.push !== false);
        setPorCanal(dados.porCanal);
        setOpcoesDisponiveis(dados.opcoesDisponiveis);
      })
      .catch(() => {
        if (vivo) setErro(traduzir("Não foi possível carregar suas preferências de notificações."));
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [ativo, traduzir]);

  const opcoesVisiveis = useMemo(
    () => OPCOES.filter((opcao) => opcoesDisponiveis.includes(opcao.chave)),
    [opcoesDisponiveis]
  );

  /* Canal só aparece com a API dele liberada na empresa: com e-mail ou WhatsApp
     desligados o canal some daqui, não há como escolher o que não existe. */
  const canaisVisiveis = useMemo(
    () =>
      CANAIS.filter((canal) => {
        if (canal.chave === "email") return recursosApi.email.visivel;
        if (canal.chave === "whatsapp") return recursosApi.whatsapp.visivel;
        return true;
      }),
    [recursosApi]
  );

  /* "Ativas" conta cada par notificação × canal ligado, e só quando o canal
     está ligado no topo — com o canal desligado nada sai por ele. */
  const totalAtivas = useMemo(() => {
    if (!porCanal) return 0;
    return opcoesVisiveis.reduce((total, opcao) => {
      const linha = porCanal[opcao.chave];
      if (!linha) return total;
      return total + canaisVisiveis.filter((canal) => canais[canal.chave] && linha[canal.chave]).length;
    }, 0);
  }, [canais, canaisVisiveis, opcoesVisiveis, porCanal]);

  const canaisLigados = canaisVisiveis.filter((canal) => canais[canal.chave]).length;

  function alternarItem(chave: ChaveNotificacaoDisponivel, canal: CanalNotificacao, valor: boolean) {
    setPorCanal((atual) => (atual ? { ...atual, [chave]: { ...atual[chave], [canal]: valor } } : atual));
  }

  async function salvar() {
    if (!porCanal) return;
    setSalvando(true);
    setErro("");
    try {
      const resposta = await salvarPreferenciasNotificacoesUsuario({ canais, porCanal, somAtivo });
      if (resposta.sucesso === false) throw new Error(resposta.erro);
      if (resposta.canais) {
        setCanais(resposta.canais);
        definirPushNotificacoesAtivo(resposta.canais.push !== false);
      }
      if (typeof resposta.somAtivo === "boolean") {
        setSomAtivo(resposta.somAtivo);
        definirSomNotificacoesAtivo(resposta.somAtivo);
      }
      if (resposta.porCanal) setPorCanal(resposta.porCanal);
      onFechar?.();
    } catch {
      setErro(traduzir("Não foi possível salvar suas preferências de notificações."));
    } finally {
      setSalvando(false);
    }
  }

  if (carregando || !porCanal) {
    return <div className={styles.estado}>{traduzir("Carregando preferências...")}</div>;
  }

  return (
    <div className={styles.conteudo} aria-busy={salvando}>
      <div className={styles.topo}>
        <section className={styles.cartaoCanais}>
          <h3 className={styles.blocoTitulo}>{traduzir("Canais de notificação")}</h3>
          <p className={styles.blocoTexto}>
            {traduzir("Ative ou desative os canais pelos quais deseja receber avisos da plataforma.")}
          </p>
          <div className={styles.listaCanais}>
            {canaisVisiveis.map((canal) => (
              <Fragment key={canal.chave}>
                <div className={styles.canal}>
                  <span className={styles.canalIcone} aria-hidden="true">
                    <canal.Icone size={18} />
                  </span>
                  <span className={styles.canalRotulo}>{traduzir(canal.rotulo)}</span>
                  <TogglePadrao
                    label=""
                    checked={canais[canal.chave]}
                    onChange={(valor) => setCanais((atual) => ({ ...atual, [canal.chave]: valor }))}
                  />
                </div>
                {canal.chave === "push" && (
                  <div className={styles.canal}>
                    <span className={styles.canalIcone} aria-hidden="true"><Volume2 size={18} strokeWidth={1.8} /></span>
                    <span className={styles.canalRotulo}>{traduzir("Som")}</span>
                    <TogglePadrao label="" checked={somAtivo} onChange={setSomAtivo} />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </section>

        <aside className={styles.cartaoResumo}>
          <strong className={styles.resumoValor}>{totalAtivas}</strong>
          <span className={styles.resumoRotulo}>{traduzir("notificações ativas")}</span>
          <span className={styles.resumoDetalhe}>
            {opcoesVisiveis.length} {traduzir("por canal configurado")} · {canaisLigados}{" "}
            {traduzir("canal(is)")}
          </span>
        </aside>
      </div>

      <section className={styles.cartaoDetalhes}>
        <header className={styles.detalhesTopo}>
          <div className={styles.detalhesTextos}>
            <h3 className={styles.blocoTitulo}>{traduzir("Preferências detalhadas")}</h3>
            <p className={styles.blocoTexto}>
              {traduzir("Escolha quais notificações deseja receber em cada canal.")}
            </p>
          </div>
          <div className={styles.colunasCanais} aria-hidden="true">
            {canaisVisiveis.map((canal) => (
              <span key={canal.chave} className={styles.colunaCanal}>
                <canal.Icone size={16} />
                {traduzir(canal.rotulo)}
              </span>
            ))}
          </div>
        </header>

        <div className={styles.lista}>
          {opcoesVisiveis.map((opcao) => (
            <div className={styles.linha} key={opcao.chave}>
              <span className={styles.linhaIcone} aria-hidden="true">
                <opcao.Icone size={16} />
              </span>
              <span className={styles.linhaTextos}>
                <strong>{traduzir(opcao.titulo)}</strong>
                <small>{traduzir(opcao.descricao)}</small>
              </span>
              <span className={styles.linhaToggles}>
                {canaisVisiveis.map((canal) => (
                  <TogglePadrao
                    key={canal.chave}
                    label=""
                    checked={porCanal[opcao.chave]?.[canal.chave] ?? false}
                    disabled={!canais[canal.chave]}
                    onChange={(valor) => alternarItem(opcao.chave, canal.chave, valor)}
                  />
                ))}
              </span>
            </div>
          ))}
          {opcoesVisiveis.length === 0 && (
            <div className={styles.estadoVazio}>
              {traduzir("Nenhuma notificação está disponível para as telas liberadas no seu perfil.")}
            </div>
          )}
        </div>
      </section>

      {erro && (
        <div className={styles.erro} role="alert">
          {erro}
        </div>
      )}

      <div className={styles.rodape}>
        <BotaoContorno estilo="topo" onClick={() => onFechar?.()}>
          Cancelar
        </BotaoContorno>
        <BotaoTopoPrimario
          iconeInicio={<Check size={16} />}
          carregando={salvando}
          onClick={() => void salvar()}
        >
          Salvar preferências
        </BotaoTopoPrimario>
      </div>
    </div>
  );
}
